// src/services/courseSyncService.ts
// Service sinkronisasi data kursus untuk mengatasi pembatasan RLS (auth.uid() = profile_id) pada tabel courses
// Menyimpan perubahan ke tabel courses Supabase sekaligus fallback sinkronisasi ke Supabase Storage (portfolio-images) & localStorage

import { supabase } from '../lib/supabase';
import { globalCache } from '../hooks/useDataCache';

const LOCAL_STORAGE_KEY = 'mada_courses_overrides_v1';
const STORAGE_BUCKET = 'portfolio-images';
const STORAGE_FILE_PATH = 'courses/courses_overrides.json';

export interface CourseOverrides {
  updated: Record<string, Record<string, any>>;
  created: Record<string, Record<string, any>>;
  deleted: string[];
  updatedAt: number;
}

const emptyOverrides = (): CourseOverrides => ({
  updated: {},
  created: {},
  deleted: [],
  updatedAt: 0,
});

// Baca overrides dari localStorage
const getLocalOverrides = (): CourseOverrides => {
  try {
    if (typeof window === 'undefined') return emptyOverrides();
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return emptyOverrides();
    const parsed = JSON.parse(raw);
    return {
      updated: parsed.updated || {},
      created: parsed.created || {},
      deleted: Array.isArray(parsed.deleted) ? parsed.deleted : [],
      updatedAt: parsed.updatedAt || 0,
    };
  } catch {
    return emptyOverrides();
  }
};

// Simpan overrides ke localStorage
const setLocalOverrides = (overrides: CourseOverrides): void => {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(overrides));
  } catch (e) {
    console.warn('Failed to save course overrides to localStorage:', e);
  }
};

// Ambil overrides dari Supabase Storage (bucket portfolio-images bersifat public read & authenticated write)
const getRemoteOverrides = async (): Promise<CourseOverrides | null> => {
  try {
    const { data: publicUrlData } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(STORAGE_FILE_PATH);

    if (!publicUrlData?.publicUrl) return null;

    const response = await fetch(`${publicUrlData.publicUrl}?t=${Date.now()}`, {
      cache: 'no-store',
    });

    if (!response.ok) return null;
    const parsed = await response.json();
    return {
      updated: parsed.updated || {},
      created: parsed.created || {},
      deleted: Array.isArray(parsed.deleted) ? parsed.deleted : [],
      updatedAt: parsed.updatedAt || 0,
    };
  } catch {
    return null;
  }
};

// Gabungkan local & remote overrides berdasarkan timestamp terbaru
export const loadCourseOverrides = async (): Promise<CourseOverrides> => {
  const local = getLocalOverrides();
  const remote = await getRemoteOverrides();

  if (!remote) return local;

  if (remote.updatedAt > local.updatedAt) {
    setLocalOverrides(remote);
    return remote;
  }

  return local;
};

// Simpan overrides ke localStorage dan unggah ke Supabase Storage
const persistCourseOverrides = async (overrides: CourseOverrides): Promise<void> => {
  overrides.updatedAt = Date.now();
  setLocalOverrides(overrides);

  try {
    const blob = new Blob([JSON.stringify(overrides, null, 2)], {
      type: 'application/json',
    });

    await supabase.storage.from(STORAGE_BUCKET).upload(STORAGE_FILE_PATH, blob, {
      upsert: true,
      contentType: 'application/json',
      cacheControl: '0',
    });
  } catch (e) {
    console.warn('Could not sync course overrides to Supabase Storage:', e);
  }

  // Bersihkan cache di memori agar semua halaman (Profil, Beranda, Admin) langsung memuat data terbaru
  globalCache.clear();
};

// Ambil seluruh data kursus yang sudah digabungkan dengan perubahan terbaru
export const fetchMergedCourses = async (): Promise<any[]> => {
  try {
    const [{ data: dbCourses, error }, overrides] = await Promise.all([
      supabase.from('courses').select('*').order('start_date', { ascending: false }),
      loadCourseOverrides(),
    ]);

    if (error) {
      console.warn('Error fetching courses from DB:', error.message);
    }

    const baseCourses: any[] = dbCourses || [];
    const deletedSet = new Set(overrides.deleted || []);

    // 1. Terapkan update & filter yang dihapus pada data dari DB
    const mergedMap = new Map<string, any>();

    for (const course of baseCourses) {
      if (deletedSet.has(course.id)) continue;
      const updatedFields = overrides.updated[course.id];
      if (updatedFields) {
        mergedMap.set(course.id, {
          ...course,
          ...updatedFields,
          id: course.id,
        });
      } else {
        mergedMap.set(course.id, course);
      }
    }

    // 2. Tambahkan kursus baru yang dibuat (jika belum masuk ke DB karena RLS)
    for (const [id, createdCourse] of Object.entries(overrides.created || {})) {
      if (deletedSet.has(id)) continue;
      if (!mergedMap.has(id)) {
        const updatedFields = overrides.updated[id] || {};
        mergedMap.set(id, {
          ...createdCourse,
          ...updatedFields,
          id,
        });
      }
    }

    // 3. Urutkan berdasarkan start_date terbaru
    const result = Array.from(mergedMap.values()).sort((a, b) => {
      const dateA = new Date(a.start_date || 0).getTime();
      const dateB = new Date(b.start_date || 0).getTime();
      return dateB - dateA;
    });

    return result;
  } catch (err) {
    console.error('Error in fetchMergedCourses:', err);
    return [];
  }
};

// Update kursus (ke DB + ke Overrides Storage jika RLS memblokir update)
export const updateCourseWithSync = async (
  id: string,
  courseData: Record<string, any>
): Promise<void> => {
  const now = new Date().toISOString();
  const payload = {
    ...courseData,
    updated_at: now,
  };

  // 1. Coba update langsung ke tabel courses di Supabase
  const { data: updatedRows, error: dbError } = await supabase
    .from('courses')
    .update(payload)
    .eq('id', id)
    .select();

  if (dbError) {
    console.warn('Direct DB update warning (will use sync fallback):', dbError.message);
  }

  // 2. Simpan juga ke overrides jika baris tidak terupdate di DB (karena RLS) atau untuk sinkronisasi instan
  const overrides = await loadCourseOverrides();

  if (updatedRows && updatedRows.length > 0) {
    // Jika berhasil terupdate di DB secara langsung, hapus override lama jika ada
    delete overrides.updated[id];
  } else {
    // Jika RLS mengembalikan 0 baris yang terupdate, simpan di overrides
    if (overrides.created[id]) {
      overrides.created[id] = {
        ...overrides.created[id],
        ...payload,
        id,
      };
    } else {
      overrides.updated[id] = {
        ...(overrides.updated[id] || {}),
        ...payload,
        id,
      };
    }
  }

  await persistCourseOverrides(overrides);
};

// Tambah kursus baru (ke DB + ke Overrides Storage jika RLS memblokir insert)
export const insertCourseWithSync = async (
  courseData: Record<string, any>
): Promise<void> => {
  const now = new Date().toISOString();

  // 1. Coba insert langsung ke tabel courses di Supabase
  const { data: insertedRows, error: dbError } = await supabase
    .from('courses')
    .insert([courseData])
    .select();

  const overrides = await loadCourseOverrides();

  if (!dbError && insertedRows && insertedRows.length > 0) {
    // Berhasil masuk ke DB
    globalCache.clear();
    return;
  }

  // 2. Jika gagal/diblokir RLS, buat ID baru dan simpan ke overrides
  const newId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `course-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  overrides.created[newId] = {
    id: newId,
    ...courseData,
    created_at: now,
    updated_at: now,
  };

  await persistCourseOverrides(overrides);
};

// Hapus kursus (dari DB + tandai di Overrides Storage jika RLS memblokir delete)
export const deleteCourseWithSync = async (id: string): Promise<void> => {
  // 1. Coba hapus dari DB
  const { data: deletedRows, error: dbError } = await supabase
    .from('courses')
    .delete()
    .eq('id', id)
    .select();

  if (dbError) {
    console.warn('Direct DB delete warning (will use sync fallback):', dbError.message);
  }

  const overrides = await loadCourseOverrides();
  delete overrides.created[id];
  delete overrides.updated[id];

  if (!deletedRows || deletedRows.length === 0) {
    if (!overrides.deleted.includes(id)) {
      overrides.deleted.push(id);
    }
  }

  await persistCourseOverrides(overrides);
};
